import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 0.75rem;
  max-width: 56rem;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-10px) scale(0.98);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background: #f9fafb;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.2;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 0.375rem;

  &:hover {
    color: #1e40af;
    background: #f3f4f6;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(30, 64, 175, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
`;

const List = styled.ul`
  list-style-type: disc;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #4b5563;
  margin: 0;
`;

const ListItem = styled.li`
  font-size: 0.8125rem;
  line-height: 1.4;
  
  &::marker {
    color: #6b7280;
  }
`;

const Paragraph = styled.p`
  color: #4b5563;
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const CheckboxContainer = styled.div`
  border-top: 1px solid #f1f5f9;
  padding-top: 1.25rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #374151;
  cursor: pointer;
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const CheckboxInput = styled.input`
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  accent-color: #1e40af;
  margin-top: 0.125rem;
  flex-shrink: 0;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(30, 64, 175, 0.2);
  }
`;

const ModalFooter = styled.div`
  border-top: 1px solid #f1f5f9;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const CancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: transparent;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  font-weight: 500;
  font-size: 0.875rem;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(30, 64, 175, 0.2);
  }
`;

const StartScanButton = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  border: none;
  color: #ffffff;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  font-weight: 500;
  font-size: 0.875rem;
  background: ${({ disabled }) =>
    disabled
      ? '#9ca3af'
      : 'linear-gradient(90deg, #1e40af 0%, #3b82f6 100%)'};
  box-shadow: ${({ disabled }) =>
    disabled
      ? 'none'
      : '0 2px 4px rgba(30, 64, 175, 0.2)'};

  &:hover {
    ${({ disabled }) =>
      !disabled && `
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
      `}
  }

  &:active {
    ${({ disabled }) =>
      !disabled && `
        transform: translateY(0);
      `}
  }

  &:focus {
    outline: none;
    ${({ disabled }) =>
      !disabled && `
        box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.2);
      `}
  }
`;

export function TermsModal({ isOpen, onClose, onAccept }) {
  const [accepted, setAccepted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (accepted) {
      onAccept();
      onClose();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>
            Terms & Conditions for Penetration Testing Scans
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          <ContentWrapper>
            <Section>
              <SectionTitle>Authorization & Ownership</SectionTitle>
              <List>
                <ListItem>
                  By initiating a scan, you confirm that you are the legitimate
                  owner of the target asset(s) or have obtained explicit written
                  permission from the asset owner(s) to perform security testing.
                </ListItem>
                <ListItem>
                  You acknowledge that conducting penetration tests on systems
                  you do not own or lack authorization for is strictly prohibited
                  and may be illegal.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>Scope & Limitations</SectionTitle>
              <List>
                <ListItem>
                  The scanning process may include, but is not limited to, tests
                  such as vulnerability detection, port scanning, directory
                  traversal, file upload checks, insecure deserialization checks,
                  and clickjacking assessments.
                </ListItem>
                <ListItem>
                  <strong>Important:</strong> Our automated scans do not perform or 
                  detect Business Logic Bugs/Attacks. These require manual testing 
                  and contextual understanding of the specific application workflow.
                </ListItem>
                <ListItem>
                  You understand and accept that these scans may temporarily
                  disrupt or degrade the performance of the target system(s) due
                  to the nature of penetration testing activities.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>Legal Compliance</SectionTitle>
              <List>
                <ListItem>
                  You agree to comply with all applicable local, state, national,
                  and international laws and regulations regarding the use of
                  penetration testing tools.
                </ListItem>
                <ListItem>
                  You are solely responsible for any legal consequences arising
                  from unauthorized or illegal use of this service.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>Data Collection & Privacy</SectionTitle>
              <List>
                <ListItem>
                  Certain technical data (e.g., scan results, system logs) may be
                  collected to facilitate accurate and thorough testing.
                </ListItem>
                <ListItem>
                  We respect your privacy and will not share or sell collected
                  data to third parties, except as required by law or with your
                  explicit consent.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>Liability & Indemnification</SectionTitle>
              <List>
                <ListItem>
                  You acknowledge that vulnerabilities discovered during the scan
                  are not created by our service but are inherent to the tested
                  system(s). We cannot guarantee that every potential vulnerability
                  will be identified.
                </ListItem>
                <ListItem>
                  We are not liable for any direct, indirect, incidental, or
                  consequential damages resulting from the use of this tool,
                  including loss of data, service interruptions, or system
                  downtime.
                </ListItem>
                <ListItem>
                  You agree to indemnify, defend, and hold harmless our company
                  and its affiliates from any claims, damages, or liabilities
                  arising from your use or misuse of this service.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>User Responsibilities</SectionTitle>
              <List>
                <ListItem>
                  You are responsible for remediating any vulnerabilities
                  identified during the scan.
                </ListItem>
                <ListItem>
                  You agree not to use any information gained from this tool for
                  malicious or illegal activities.
                </ListItem>
                <ListItem>
                  You will not hold our company responsible for any unauthorized
                  or unethical use of the information obtained through the scans.
                </ListItem>
              </List>
            </Section>

            <Section>
              <SectionTitle>Acceptance of Risks</SectionTitle>
              <Paragraph>
                By proceeding with the scan, you understand that security testing
                inherently carries certain risks (e.g., system crashes or data
                corruption) and that you accept these risks.
              </Paragraph>
            </Section>
          </ContentWrapper>

          <CheckboxContainer>
            <CheckboxLabel>
              <CheckboxInput
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>I have read, understood, and agree to all the Terms & Conditions above</span>
            </CheckboxLabel>
          </CheckboxContainer>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <StartScanButton
            onClick={handleAccept}
            disabled={!accepted}
          >
            Start Scan
          </StartScanButton>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
}